import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { fetchTestimonials, fetchReviewStats } from "../lib/api";

const FALLBACK_STATS = { average_rating: 4.9, review_count: 55, source: "Google Reviews" };

function Stars({ rating = 5, size = "md" }) {
  const cls = size === "sm" ? "h-3 w-3" : size === "lg" ? "h-5 w-5" : "h-4 w-4";
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`${cls} ${
            i < Math.round(rating)
              ? "fill-[#FF8C42] text-[#FF8C42]"
              : "text-slate-300"
          }`}
        />
      ))}
    </div>
  );
}

export default function ReviewsSection({
  programSlug,
  limit = 3,
  title = "Loved by families in Richmond BC",
  subtitle = "Real words from parents whose kids walk through our doors every week.",
  variant = "section",
}) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(FALLBACK_STATS);
  const [active, setActive] = useState(0);

  useEffect(() => {
    fetchTestimonials()
      .then((arr) => {
        const filtered = programSlug
          ? arr.filter((t) => t.related_program === programSlug)
          : arr;
        const list = filtered.length ? filtered : arr;
        setItems(list.slice(0, Math.max(limit, list.length)));
      })
      .catch(() => {});
    fetchReviewStats()
      .then((s) => setStats({ ...FALLBACK_STATS, ...s }))
      .catch(() => {});
  }, [programSlug, limit]);

  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => setActive((a) => (a + 1) % items.length), 6000);
    return () => clearInterval(id);
  }, [items.length]);

  if (!items.length) return null;

  const current = items[active];

  const inner = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          {title && (
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-extrabold text-slate-900 leading-[1.1]">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl text-sm sm:text-base">
              {subtitle}
            </p>
          )}
        </div>
        <div
          className="flex items-center gap-3 rounded-full bg-white border border-slate-200 px-5 py-2.5 shadow-sm"
          data-testid="reviews-aggregate"
        >
          <Stars rating={stats.average_rating} size="md" />
          <div className="flex items-baseline gap-1.5">
            <span className="font-display font-extrabold text-slate-900">
              {Number(stats.average_rating).toFixed(1)}
            </span>
            <span className="text-xs text-slate-500">
              from {stats.review_count} {stats.source}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div
          key={current.id}
          className="lg:col-span-7 rounded-[2rem] bg-white border border-slate-200/70 p-8 sm:p-10 shadow-sm"
          data-testid="reviews-featured"
        >
          <Stars rating={current.rating || 5} size="md" />
          <p className="mt-5 font-display text-xl sm:text-2xl text-slate-900 leading-snug">
            “{current.quote}”
          </p>
          <p className="mt-5 text-sm font-semibold text-slate-700">
            — {current.name || "Nexora parent"}
          </p>
        </div>
        <div className="lg:col-span-5 grid gap-3 content-start">
          {items.slice(0, 4).map((t, i) => (
            <button
              key={t.id || i}
              onClick={() => setActive(i)}
              className={`text-left rounded-2xl border p-5 transition-colors ${
                i === active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`reviews-thumb-${i}`}
            >
              <Stars rating={t.rating || 5} size="sm" />
              <p
                className={`mt-3 text-sm leading-snug line-clamp-3 ${
                  i === active ? "text-slate-100" : "text-slate-700"
                }`}
              >
                {t.quote}
              </p>
              <p
                className={`mt-2 text-xs font-semibold ${
                  i === active ? "text-slate-300" : "text-slate-500"
                }`}
              >
                {t.name || "Nexora parent"}
              </p>
            </button>
          ))}
        </div>
      </div>
    </>
  );

  if (variant === "embedded") {
    return (
      <div data-testid="reviews-section" className="">
        {inner}
      </div>
    );
  }
  return (
    <section
      data-testid="reviews-section"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24"
    >
      {inner}
    </section>
  );
}

export { Stars };
